// Pictures Claude made for the person following a ticket: Mermaid diagrams,
// page mockups (static HTML), drawings and screenshots, left in the
// worktree's .trisof/outbox/ and uploaded by the box after the turn.
// A gallery of cards, a full-screen viewer, and options side by side.
import { useEffect, useId, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import ReactMarkdown from 'react-markdown'
import { ChevronLeft, ChevronRight, Columns2, ExternalLink, Monitor, Smartphone, X } from 'lucide-react'
import type { Visual, VisualList } from '@one-deployment/api'
import { Age } from '@/components/status'
import { Button as SButton } from '@/components/shadcn/button'
import { enc, get } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useLiveQueryOpts } from '@/lib/live'

export function useVisuals(workspaceId: string | undefined) {
  const live = useLiveQueryOpts(workspaceId ? `workspace:${workspaceId}` : null)
  return useQuery({
    queryKey: ['ws', workspaceId, 'visuals'],
    queryFn: ({ signal }) => get<VisualList>(`/api/workspaces/${enc(workspaceId!)}/visuals`, signal),
    enabled: !!workspaceId,
    ...live,
  })
}

/** The text of a diagram or note; pictures and pages load straight from their URL. */
function useText(v: Visual) {
  return useQuery({
    queryKey: ['visual-text', v.id],
    queryFn: async ({ signal }) => {
      const r = await fetch(v.url, { credentials: 'same-origin', signal })
      if (!r.ok) throw new Error(r.status === 404 ? 'This picture has expired' : `Could not load it (${r.status})`)
      return r.text()
    },
    enabled: v.format === 'mermaid' || v.format === 'markdown',
    staleTime: Infinity,
  })
}

const LABEL: Record<Visual['format'], string> = { mermaid: 'Diagram', svg: 'Drawing', html: 'Mockup', image: 'Screenshot', markdown: 'Note' }

/** `%% title: Checkout flow` on a diagram's first lines names it better than its file. */
export function mermaidTitle(code: string | undefined): string | null {
  const m = code?.match(/^\s*%%\s*title:\s*(.+)$/m)
  return m ? m[1]!.trim() : null
}

/** option-a-…, option-b-…: the files of one choice, shown side by side. */
export function optionGroup(vs: Visual[]): Visual[] {
  const opts = vs.filter((v) => /^option[-_ ]?(?:[a-z]|\d{1,2})[-_.]/i.test(v.name))
  if (opts.length >= 2) return [...opts].sort((a, b) => a.name.localeCompare(b.name))
  // before-…/after-…: a finished change, shown as a pair (nothing to choose).
  const before = vs.find((v) => /^before[-_.]/i.test(v.name)), after = vs.find((v) => /^after[-_.]/i.test(v.name))
  return before && after ? [before, after] : []
}

/** The option's own words: `option-b-ops-and-managers.html` → "Ops and managers". */
export function optionTitle(v: Visual): string {
  const t = v.title.replace(/^(option\s*(?:[a-z]|\d{1,2})|before|after)\s+/i, '')
  return t ? t[0]!.toUpperCase() + t.slice(1) : v.title
}

export function optionLabel(v: Visual): string {
  const m = v.name.match(/^option[-_ ]?([a-z]|\d{1,2})[-_.]/i)
  if (m) return `Option ${m[1]!.toUpperCase()}`
  const ba = v.name.match(/^(before|after)[-_.]/i)?.[1]
  return ba ? ba[0]!.toUpperCase() + ba.slice(1).toLowerCase() : v.title
}

let mermaidReady: Promise<typeof import('mermaid').default> | null = null
function loadMermaid() {
  mermaidReady ??= import('mermaid').then(({ default: m }) => {
    const css = getComputedStyle(document.documentElement)
    const c = (n: string, d: string) => css.getPropertyValue(`--color-${n}`).trim() || d
    m.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      theme: 'base',
      fontFamily: 'Inter Variable, Inter, system-ui, sans-serif',
      themeVariables: {
        darkMode: true,
        background: c('surface-1', '#0f1011'),
        primaryColor: c('surface-3', '#1c1d20'),
        primaryBorderColor: c('accent', '#5e6ad2'),
        primaryTextColor: c('fg', '#f7f8f8'),
        secondaryColor: c('accent-tint', '#232a4a'),
        tertiaryColor: c('surface-2', '#151618'),
        lineColor: c('fg-subtle', '#8a8f98'),
        textColor: c('fg-muted', '#b4b6bc'),
        noteBkgColor: c('surface-4', '#232428'),
        noteTextColor: c('fg', '#f7f8f8'),
        noteBorderColor: c('border-strong', '#2e3036'),
        fontSize: '14px',
      },
      flowchart: { curve: 'basis', padding: 14 },
    })
    return m
  })
  return mermaidReady
}

/**
 * Quote flowchart node labels that are not quoted yet: brackets inside a label
 * (`D{Ben (manager) approves?}`) break Mermaid's parser, and quoting is always
 * safe. Tried only when the diagram as written fails. Same as the box's export.mjs.
 */
/** State and sequence diagrams draw quotes literally; drop ones wrapping a whole label. */
export function unquoteLabels(code: string): string {
  if (!/^\s*(stateDiagram|sequenceDiagram)/m.test(code)) return code
  return code.replace(/(:\s*)"([^"\n]*)"\s*$/gm, '$1$2').replace(/^(\s*)"([^"\n]*)"\s*$/gm, '$1$2')
}

export function quoteLabels(code: string): string {
  const shapes: Array<[string, string]> = [['[[', ']]'], ['((', '))'], ['{{', '}}'], ['([', '])'], ['[(', ')]'], ['[', ']'], ['(', ')'], ['{', '}']]
  const esc = (x: string) => x.replace(/[[\](){}]/g, '\\$&')
  return code.split('\n').map((line) => {
    if (/^\s*(%%|classDef|class |style |linkStyle|click |subgraph|end\b|direction|flowchart|graph)/.test(line)) return line
    let out = line
    for (const [o, c] of shapes) {
      const re = new RegExp(`(\\b[A-Za-z0-9_]+)${esc(o)}(?!["[({])((?:(?!${esc(c)}).)+?)${esc(c)}`, 'g')
      out = out.replace(re, (_, id: string, label: string) => `${id}${o}"${label.replace(/"/g, '#quot;')}"${c}`)
    }
    return out
  }).join('\n')
}

function Mermaid({ code, className }: { code: string; className?: string }) {
  const id = `m${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  const [out, setOut] = useState<{ svg?: string; error?: string }>({})
  useEffect(() => {
    let live = true
    loadMermaid()
      .then((m) => m.render(id, unquoteLabels(code)).catch(() => m.render(`${id}q`, quoteLabels(code))))
      .then((r) => live && setOut({ svg: r.svg }))
      .catch((e: unknown) => live && setOut({ error: e instanceof Error ? e.message.split('\n')[0] : 'This diagram has a mistake' }))
    return () => { live = false }
  }, [id, code])
  if (out.error) {
    return (
      <div className={cn('flex flex-col gap-2 p-3 text-xs', className)}>
        <span className="text-warn">Could not draw this diagram: {out.error}</span>
        <pre className="max-h-64 overflow-auto rounded-sm bg-surface-2 p-2 font-mono text-[11px] text-fg-muted">{code}</pre>
      </div>
    )
  }
  if (!out.svg) return <div className={cn('animate-pulse bg-surface-2', className)} />
  // Mermaid in strict mode escapes labels and drops script; the SVG is its own.
  return <div className={cn('flex items-center justify-center [&_svg]:h-full [&_svg]:max-h-full [&_svg]:w-full [&_svg]:!max-w-full', className)} dangerouslySetInnerHTML={{ __html: out.svg }} />
}

/** A static page shown at a real width and scaled to fit its box. Sandboxed twice: here and by the server. */
function Page({ v, width, className }: { v: Visual; width?: number; className?: string }) {
  const [box, setBox] = useState<HTMLDivElement | null>(null)
  const [fit, setFit] = useState({ scale: 0, width: 0 })
  useEffect(() => {
    if (!box) return
    // A set width (a phone) is shown as it is; otherwise the page gets the box's width, never under a tablet's (mockups use a ~720px column).
    const ro = new ResizeObserver(([e]) => {
      const room = e!.contentRect.width
      const w = width ?? Math.max(room, 800)
      setFit({ scale: Math.min(1, room / w), width: w })
    })
    ro.observe(box)
    return () => ro.disconnect()
  }, [box, width])
  const { scale } = fit
  return (
    <div ref={setBox} className={cn('relative overflow-hidden bg-white', className)}>
      {scale > 0 && (
        <iframe
          title={v.title}
          src={v.url}
          sandbox=""
          loading="lazy"
          className="pointer-events-auto absolute left-0 top-0 origin-top-left border-0"
          style={{ width: fit.width, height: `${100 / scale}%`, transform: `scale(${scale})` }}
        />
      )}
    </div>
  )
}

/** One picture, filling its box. */
function Body({ v, width, className, still }: { v: Visual; width?: number; className?: string; still?: boolean }) {
  const text = useText(v)
  if (v.format === 'html') return <Page v={v} width={width} className={cn(className, still && 'pointer-events-none')} />
  if (v.format === 'svg' || v.format === 'image') {
    return <div className={cn('flex items-center justify-center', className)}><img src={v.url} alt={v.title} loading="lazy" className="max-h-full max-w-full object-contain" /></div>
  }
  if (text.error) return <div className={cn('p-3 text-xs text-warn', className)}>{String((text.error as Error).message)}</div>
  if (text.data === undefined) return <div className={cn('animate-pulse bg-surface-2', className)} />
  if (v.format === 'mermaid') return <Mermaid code={text.data} className={className} />
  return (
    <div className={cn('overflow-auto p-4 text-sm leading-relaxed text-fg-muted [&_code]:rounded-xs [&_code]:bg-surface-3 [&_code]:px-1 [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-fg [&_h2]:mb-1 [&_h2]:mt-3 [&_h2]:font-semibold [&_h2]:text-fg [&_li]:ml-4 [&_li]:list-disc [&_p]:mb-2', className)}>
      <ReactMarkdown>{text.data}</ReactMarkdown>
    </div>
  )
}

function Title({ v, option }: { v: Visual; option?: boolean }) {
  const text = useText(v)
  return <>{(v.format === 'mermaid' && mermaidTitle(text.data)) || (option ? optionTitle(v) : v.title)}</>
}

/** The answer an option's picture stands for: "B: …" for option-b-…, else the same place in the list. */
export function answerFor(v: Visual, group: Visual[], answers: string[]): string | null {
  const letter = v.name.match(/^option[-_ ]?([a-z]|\d{1,2})[-_.]/i)?.[1]?.toUpperCase()
  if (!letter || !answers.length) return null
  const named = answers.find((a) => new RegExp(`^\\(?(option\\s+)?${letter}\\b\\s*[:).-]`, 'i').test(a.trim()))
  return named ?? answers[group.indexOf(v)] ?? null
}

export type Answering = { options: string[]; pick: (answer: string) => void; disabled?: boolean; picked?: string | null }

function Choose({ answer, answering, label }: { answer: string | null; answering?: Answering; label: string }) {
  if (!answer || !answering) return null
  const chosen = answering.picked === answer
  return (
    <SButton
      size="xs"
      disabled={answering.disabled}
      title={answer}
      onClick={(e) => { e.stopPropagation(); answering.pick(answer) }}
      className={cn('h-5 shrink-0 rounded-sm px-2 text-2xs font-semibold shadow-sm', chosen ? 'bg-ok text-white hover:bg-ok' : 'bg-fg text-black hover:bg-white')}
    >
      {chosen ? 'Chosen' : `Choose ${label.replace(/^Option\s*/, '')}`}
    </SButton>
  )
}

function Card({ v, label, onOpen, answer, answering, compact }: { v: Visual; label?: string; onOpen: () => void; answer?: string | null; answering?: Answering; compact?: boolean }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen())}
      className="group flex min-w-0 cursor-zoom-in flex-col overflow-hidden rounded-lg border border-border bg-surface-2 text-left transition hover:border-accent/60 hover:shadow-[0_0_0_1px_var(--color-accent-tint)] focus-visible:border-accent"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden border-b border-border bg-surface-2">
        <Body v={v} still width={v.format === 'html' ? 1024 : undefined} className="absolute inset-0 h-full w-full" />
        <span className="absolute inset-0" aria-hidden />
        {label && answer && answering && <span className="absolute bottom-2 right-2"><Choose answer={answer} answering={answering} label={label} /></span>}
      </div>
      <div className="flex items-center gap-2 px-2.5 py-2">
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-fg">{label ? <span className="mr-1 font-semibold text-accent">{label.replace(/^Option\s*/, '')}</span> : null}<Title v={v} option={!!label} /></span>
        {!compact && <span className="shrink-0 rounded-xs bg-surface-3 px-1.5 py-0.5 text-2xs text-fg-subtle">{LABEL[v.format]}</span>}
      </div>
    </div>
  )
}

/**
 * The pictures of one ticket or one question. Options (option-a-…,
 * option-b-…) also open side by side.
 */
export function VisualGallery({ visuals, title, compact, className, answering }: { visuals: Visual[]; title?: string; compact?: boolean; className?: string; answering?: Answering }) {
  const [open, setOpen] = useState<number | 'compare' | null>(null)
  const options = useMemo(() => optionGroup(visuals), [visuals])
  // The group first, in its own order (A, B… or before, after), then the rest.
  const shown = useMemo(() => [...options, ...visuals.filter((v) => !options.includes(v))], [options, visuals])
  if (!visuals.length) return null
  return (
    <section className={className}>
      {(title || options.length > 0) && (
        <div className="mb-2 flex items-center gap-2">
          {title && <h3 className="text-xs font-semibold text-fg-subtle">{title}</h3>}
          <span className="flex-1" />
          {options.length > 0 && (
            <SButton variant="ghost" size="xs" onClick={() => setOpen('compare')} className="h-5 gap-1 rounded-sm px-1.5 text-2xs font-normal text-accent hover:bg-accent/10 hover:text-accent">
              <Columns2 size={12} /> {/^before/i.test(options[0]!.name) ? 'Before and after' : `Compare ${options.length} options`}
            </SButton>
          )}
        </div>
      )}
      <div className={cn('grid gap-2', compact ? 'grid-cols-2' : 'grid-cols-[repeat(auto-fill,minmax(14rem,1fr))]')}>
        {shown.map((v, i) => <Card key={v.id} v={v} label={options.includes(v) ? optionLabel(v) : undefined} answer={options.includes(v) && answering ? answerFor(v, options, answering.options) : null} answering={answering} compact={compact} onOpen={() => setOpen(i)} />)}
      </div>
      <Viewer visuals={shown} options={options} at={open} onAt={setOpen} answering={answering} />
    </section>
  )
}

/** Full screen: one picture with ← → between them, or the options side by side. */
function Viewer({ visuals, options, at, onAt, answering }: { visuals: Visual[]; options: Visual[]; at: number | 'compare' | null; onAt: (a: number | 'compare' | null) => void; answering?: Answering }) {
  const [phone, setPhone] = useState(false)
  const n = visuals.length
  useEffect(() => {
    if (typeof at !== 'number') return
    const k = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') onAt((at + 1) % n)
      if (e.key === 'ArrowLeft') onAt((at - 1 + n) % n)
    }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [at, n, onAt])
  const v = typeof at === 'number' ? visuals[at] : undefined
  const shown = at === 'compare' ? options : v ? [v] : []
  const pages = shown.some((s) => s.format === 'html')
  return (
    <DialogPrimitive.Root open={at !== null} onOpenChange={(o) => !o && onAt(null)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/75 backdrop-blur-[2px]" />
        <DialogPrimitive.Content className="fixed inset-3 z-50 flex flex-col overflow-hidden rounded-lg border border-border-strong bg-surface-3 shadow-lg focus:outline-none md:inset-6">
          <header className="flex h-11 shrink-0 items-center gap-3 border-b border-border px-4">
            <DialogPrimitive.Title className="min-w-0 flex-1 truncate text-sm font-semibold">
              {at === 'compare' ? (/^before/i.test(options[0]!.name) ? 'Before and after' : `${options.length} options side by side`) : v ? <><Title v={v} /><span className="ml-2 text-xs font-normal text-fg-subtle">{LABEL[v.format]} · <Age from={v.at} /></span></> : null}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">A picture Claude made for this ticket</DialogPrimitive.Description>
            {pages && (
              <div className="flex rounded-sm border border-border p-0.5">
                <SButton variant="ghost" size="icon-xs" aria-label="Desktop width" aria-pressed={!phone} onClick={() => setPhone(false)} className={cn('size-6 rounded-xs', !phone ? 'bg-surface-3 text-fg' : 'text-fg-subtle')}><Monitor size={13} /></SButton>
                <SButton variant="ghost" size="icon-xs" aria-label="Phone width" aria-pressed={phone} onClick={() => setPhone(true)} className={cn('size-6 rounded-xs', phone ? 'bg-surface-3 text-fg' : 'text-fg-subtle')}><Smartphone size={13} /></SButton>
              </div>
            )}
            {typeof at === 'number' && n > 1 && (
              <div className="flex items-center gap-1 text-xs text-fg-subtle">
                <SButton variant="ghost" size="icon-xs" aria-label="Previous" onClick={() => onAt((at - 1 + n) % n)} className="size-6 rounded-xs hover:bg-hover hover:text-fg"><ChevronLeft size={15} /></SButton>
                <span className="tabular-nums">{at + 1} / {n}</span>
                <SButton variant="ghost" size="icon-xs" aria-label="Next" onClick={() => onAt((at + 1) % n)} className="size-6 rounded-xs hover:bg-hover hover:text-fg"><ChevronRight size={15} /></SButton>
              </div>
            )}
            {v && <a href={v.url} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-xs text-fg-subtle hover:text-fg">Open <ExternalLink size={11} /></a>}
            <DialogPrimitive.Close aria-label="Close" className="rounded-xs p-1 text-fg-subtle hover:bg-hover hover:text-fg"><X size={15} /></DialogPrimitive.Close>
          </header>
          <div className={cn('grid min-h-0 flex-1 gap-px bg-border', shown.length > 1 && (shown.length === 2 || shown.length === 4 ? 'grid-cols-2' : 'grid-cols-2 xl:grid-cols-3'))}>
            {shown.map((s) => (
              <div key={s.id} className="flex min-h-0 flex-col bg-bg">
                {(at === 'compare' || options.includes(s)) && (
                  <div className="flex items-center gap-2 border-b border-border px-3 py-1.5 text-xs">
                    <span className="min-w-0 flex-1 truncate"><span className="font-semibold text-accent">{optionLabel(s)}</span> <span className="text-fg-muted">· <Title v={s} option /></span></span>
                    <Choose answer={answering ? answerFor(s, options, answering.options) : null} answering={answering} label={optionLabel(s)} />
                  </div>
                )}
                <div className="flex min-h-0 flex-1 items-stretch justify-center overflow-auto p-4">
                  <Body v={s} width={phone ? 390 : undefined} className={cn('h-full w-full', s.format === 'html' && phone && 'max-w-[390px] rounded-md border border-border-strong')} />
                </div>
              </div>
            ))}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
